import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const VISIT_KEY = "nm-install-visits";
const ADDED_KEY = "nm-install-added-cart";
const SESSION_KEY = "nm-install-visit-counted";
const MIN_VISITS = 3;

function readVisits(): number {
  try { return Number(localStorage.getItem(VISIT_KEY) || "0") || 0; } catch { return 0; }
}
function bumpVisitOnce(): number {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return readVisits();
    sessionStorage.setItem(SESSION_KEY, "1");
    const next = readVisits() + 1;
    localStorage.setItem(VISIT_KEY, String(next));
    return next;
  } catch { return readVisits(); }
}
function hasAddedToCart(): boolean {
  try { return localStorage.getItem(ADDED_KEY) === "1"; } catch { return false; }
}

/**
 * Decide where to send the user after a successful install. Captures the
 * route they were on *before* we trigger the native prompt so the post-install
 * redirect feels contextual:
 *   - viewing a product (/detail/...) → checkout if they already added to cart,
 *     otherwise stay on the product page
 *   - anywhere else, but with a cart add tracked → /checkout
 *   - default → no redirect
 */
function resolvePostInstallTarget(): string | null {
  try {
    const path = window.location.pathname || "/";
    const added = hasAddedToCart();
    if (path.startsWith("/checkout")) return null;
    if (path.startsWith("/detail")) return added ? "/checkout" : path;
    if (added) return "/checkout";
    return null;
  } catch { return null; }
}

/**
 * Floating install banner.
 * Gated so it does NOT interrupt the first session:
 *   shows only after the 3rd visit OR after the user adds something to cart.
 */
export function InstallPrompt() {
  const { canInstall, showIOSHint, promptInstall, dismiss } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [eligible, setEligible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const visits = bumpVisitOnce();
    const check = () => setEligible(visits >= MIN_VISITS || hasAddedToCart());
    check();
    const onAdd = () => {
      try { localStorage.setItem(ADDED_KEY, "1"); } catch {}
      check();
    };
    window.addEventListener("nm:add-to-cart", onAdd);
    return () => window.removeEventListener("nm:add-to-cart", onAdd);
  }, []);

  useEffect(() => {
    if (!eligible) { setVisible(false); return; }
    if (canInstall || showIOSHint) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [canInstall, showIOSHint, eligible]);

  if (!visible) return null;

  const goPostInstall = () => {
    const target = resolvePostInstallTarget();
    if (target && target !== window.location.pathname) {
      try { navigate(target); } catch { /* no-op */ }
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="install-title"
      style={{
        position: "fixed",
        left: 12, right: 12, bottom: 76,
        zIndex: 90,
        maxWidth: 520, marginInline: "auto",
        background: "var(--bg2, #fff)",
        border: "1px solid var(--bd, rgba(0,0,0,0.08))",
        borderRadius: 16,
        padding: 14,
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        display: "flex", gap: 12, alignItems: "flex-start",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 40, height: 40, borderRadius: 10,
          background: "hsl(var(--primary))",
          display: "grid", placeItems: "center", color: "#fff", flexShrink: 0,
        }}
      >
        <Download size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 id="install-title" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--tx)" }}>
          Instala NutriMarket
        </h3>
        {canInstall ? (
          <>
            <p style={{ margin: "4px 0 10px", fontSize: 13, color: "var(--tx2)" }}>
              Accede más rápido y recibe avisos de pedidos y ofertas.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={async () => {
                  setVisible(false);
                  const accepted = await promptInstall();
                  if (accepted) goPostInstall();
                }}
                style={{
                  background: "hsl(var(--primary))", color: "#fff",
                  border: 0, borderRadius: 10, padding: "8px 14px",
                  fontWeight: 600, fontSize: 13, cursor: "pointer", minHeight: 36,
                }}
              >
                Instalar
              </button>
              <button
                type="button"
                onClick={() => { dismiss(); setVisible(false); }}
                style={{
                  background: "transparent", color: "var(--tx2)",
                  border: "1px solid var(--bd)", borderRadius: 10, padding: "8px 14px",
                  fontWeight: 500, fontSize: 13, cursor: "pointer", minHeight: 36,
                }}
              >
                Ahora no
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--tx2)", display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              Toca <Share size={14} aria-hidden /> <b>Compartir</b> y luego <b>“Agregar a inicio”</b>.
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        aria-label="Cerrar"
        onClick={() => { dismiss(); setVisible(false); }}
        style={{
          background: "transparent", border: 0, padding: 4, cursor: "pointer",
          color: "var(--tx2)", flexShrink: 0,
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}